import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Tag, 
  message, 
  Space,
  Descriptions,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  FolderOpenOutlined, 
  FileTextOutlined,
  DollarCircleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import dayjs from 'dayjs';
import { authService } from '../services/authService';

interface Project {
  id: number;
  name: string;
  description?: string;
  project_type: 'survey' | 'ambassador';
  status: 'active' | 'inactive' | 'completed';
  createdAt: string;
  updatedAt: string;
  labor_stats?: {
    total_count: number;
    total_amount: number;
    pending_count: number;
    settled_count: number;
  };
}

const ProjectLaborDetailIndex: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();
  const isProjectAdmin = currentUser?.role === 'project_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // 获取项目列表
  const fetchProjects = async (page = 1, size = 10) => {
    try {
      setLoading(true);
      const response = await axios.get('/projects', {
        params: {
          page,
          pageSize: size,
          include_labor_stats: true // 包含劳务统计信息
        }
      });
      
      if (response.data.success) {
        setProjects(response.data.data.list || response.data.data || []);
        setTotal(response.data.data.total || response.data.data.length);
        setCurrentPage(page);
        setPageSize(size);
      } else {
        message.error('获取项目列表失败');
      }
    } catch (error) {
      console.error('获取项目列表失败:', error);
      message.error('获取项目列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // 进入项目劳务明细管理
  const enterProjectLaborDetail = (project: Project) => {
    navigate(`/labor-detail-management/${project.id}`, {
      state: { 
        projectName: project.name,
        projectType: project.project_type 
      }
    });
  };

  // 项目状态颜色映射
  const getProjectStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'orange';
      case 'completed': return 'blue';
      default: return 'default';
    }
  };

  // 项目状态文本映射
  const getProjectStatusText = (status: string) => {
    switch (status) {
      case 'active': return '进行中';
      case 'inactive': return '暂停';
      case 'completed': return '已完成';
      default: return '未知';
    }
  };

  // 项目类型颜色映射
  const getProjectTypeColor = (type: string) => {
    switch (type) {
      case 'survey': return 'blue';
      case 'ambassador': return 'orange';
      default: return 'default';
    }
  };

  // 项目类型文本映射
  const getProjectTypeText = (type: string) => {
    switch (type) {
      case 'survey': return '调研项目';
      case 'ambassador': return '大使项目';
      default: return '未知';
    }
  };

  const columns: ColumnsType<Project> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: '项目信息',
      key: 'project_info',
      width: 300,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
            {record.name}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tag color={getProjectTypeColor(record.project_type)}>
              {getProjectTypeText(record.project_type)}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: '劳务明细统计',
      key: 'labor_stats',
      width: 200,
      align: 'center',
      render: (_, record) => {
        const stats = record.labor_stats;
        if (!stats) {
          return (
            <div style={{ color: '#999' }}>
              暂无劳务明细
            </div>
          );
        }
        
        return (
          <div>
            <Row gutter={[8, 4]}>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>总记录</div>
                <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  {stats.total_count || 0}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>总金额</div>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                  ¥{(stats.total_amount || 0).toFixed(2)}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>待处理</div>
                <div style={{ fontWeight: 'bold', color: '#faad14' }}>
                  {stats.pending_count || 0}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>已结算</div>
                <div style={{ fontWeight: 'bold', color: '#722ed1' }}>
                  {stats.settled_count || 0}
                </div>
              </Col>
            </Row>
          </div>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<FolderOpenOutlined />}
            onClick={() => enterProjectLaborDetail(record)}
          >
            劳务管理
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 美化的页面头部 */}
      <div 
        style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* 背景装饰 */}
        <div 
          style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 1
          }}
        />
        <div 
          style={{
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '150px',
            height: '150px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '50%',
            zIndex: 1
          }}
        />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '16px',
            color: 'white'
          }}>
            <FileTextOutlined style={{ fontSize: '28px', marginRight: '12px' }} />
            <div>
              <h1 style={{ 
                margin: 0, 
                color: 'white', 
                fontSize: '24px', 
                fontWeight: '600',
                marginBottom: '4px'
              }}>
                项目劳务明细管理
              </h1>
              <p style={{ 
                margin: 0, 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: '14px' 
              }}>
                选择项目进入对应的劳务明细管理页面，查看和管理劳务数据
              </p>
            </div>
          </div>
          
          {/* 快速统计信息 */}
          <div style={{ 
            display: 'flex', 
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              <div style={{ color: 'white', fontSize: '20px', fontWeight: '600' }}>
                {projects.length}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>
                总项目数
              </div>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              <div style={{ color: 'white', fontSize: '20px', fontWeight: '600' }}>
                {projects.filter(p => (p.labor_stats?.total_count || 0) > 0).length}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>
                有劳务数据
              </div>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '12px 16px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              <div style={{ color: 'white', fontSize: '20px', fontWeight: '600' }}>
                {projects.reduce((sum, p) => sum + (p.labor_stats?.pending_count || 0), 0)}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>
                待处理记录
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card 
        title="项目列表" 
        style={{ 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' 
        }}
      >

        <Table
          columns={columns}
          dataSource={projects}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个项目`,
            onChange: (page, size) => {
              fetchProjects(page, size);
            }
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default ProjectLaborDetailIndex;